import { Role, rolesManipulator } from 'auth';
import dayjs from 'dayjs';
import { FormikProvider, useFormik } from 'formik';
import omit from 'lodash/omit';
import { useCallback, useEffect, useMemo, useState } from 'react';
import * as yup from 'yup';
import IncidentTypeSelectPaginated from '@/modules/common/components/IncidentTypeSelectPaginate.tsx';
import ReportTypeSelect from '@/modules/common/components/ReportTypeSelect.tsx';
import { mapNameIdToSelectOption } from '@/modules/common/helpers/mapNameIdToSelectOption.ts';
import { useCreateReportMutation } from '@/modules/common/queries/reports.ts';
import { usePollReportUntilReady } from '@/modules/common/queries/reports.ts';
import ClientsSelectPaginated from '@/modules/admin-dashboard/components/ClientsSelectPaginated.tsx';
import DepartmentsSelectPaginated from '@/modules/admin-dashboard/components/DepartmentsSelectPaginated.tsx';
import FormSelectPaginate from '@/modules/admin-dashboard/components/FormSelectPaginate.tsx';
import RegisterSelectPaginate from '@/modules/admin-dashboard/components/RegisterSelectPaginate.tsx';
import UsersSelectPaginated from '@/modules/admin-dashboard/components/UsersSelectPaginated.tsx';
import { useGetTemplateQuery } from '@/modules/admin-dashboard/queries/reports-templates.ts';
import {
  BaseModalProps,
  Button,
  CalendarInput,
  CalendarInputProps,
  ModalContentWrapper,
  SelectButtonsNew,
  SelectPaginateOption,
  TextInputField,
} from 'ui';
import { openInNewTab, programmaticallyDownloading } from 'ui/utils';
import {
  CreateReportRequest,
  CreateReportResponse,
  ReportFormat,
  ReportStatus,
  ReportType,
  TaskResult,
} from 'compliance-api';

export const reportsFormats = [
  { label: 'PDF', value: ReportFormat.Pdf },
  { label: 'XLSX', value: ReportFormat.Xlsx },
];

const taskResultOptions = [
  { label: 'Completed', value: TaskResult.Completed },
  { label: 'Overdue', value: TaskResult.Due },
];

interface FormikValues {
  registers: SelectPaginateOption<number>[];
  users: SelectPaginateOption<number>[];
  forms: SelectPaginateOption<number>[];
  departments: SelectPaginateOption<number>[];
  incidents: SelectPaginateOption<number>[];
  client?: SelectPaginateOption<number>;
  report: Omit<CreateReportRequest, 'date_range'> & { date_range: [string, string] };
}

const defaultDates: [string, string] = [
  dayjs().subtract(1, 'month').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD'),
];

const getInitialValues = (): FormikValues => ({
  registers: [],
  users: [],
  forms: [],
  departments: [],
  incidents: [],
  client: undefined,
  report: {
    name: '',
    date_range: defaultDates,
    type: ReportType.TaskActivityReport,
    register_ids: [],
    assignee_ids: [],
    form_ids: [],
    format: ReportFormat.Pdf,
    department_ids: [],
    incident_type_ids: [],
    status: undefined,
    client_id: undefined,
  },
});

const validationSchema = yup.object({
  report: yup.object({
    name: yup
      .string()
      .trim()
      .required('Report name is required')
      .min(1, 'Report name is too short')
      .max(60, 'Report name too long'),
    start_date: yup.string().trim(),
    end_date: yup.string().trim(),
    type: yup.mixed<ReportType>().oneOf(Object.values(ReportType)).required(),
    register_ids: yup.array(),
    assignee_ids: yup.array(),
    form_ids: yup.array(),
    format: yup.mixed<ReportFormat>().oneOf(Object.values(ReportFormat)).required(),
    department_ids: yup.array(),
    incident_type_ids: yup.array(),
  }),
});

interface CreateReportModalProps extends BaseModalProps {
  templateId?: number;
  clientId?: number;
}

const CreateReportModal = ({ open, templateId, clientId, onClose }: CreateReportModalProps) => {
  const [createdReportMeta, setCreatedReportMeta] = useState<{
    id: number;
    name: string;
    type: ReportType;
    format: ReportFormat;
  } | null>(null);

  const formik = useFormik({
    initialValues: (() => {
      const initial = getInitialValues();
      return {
        ...initial,
        report: {
          ...initial.report,
          client_id: clientId,
        },
      };
    })(),
    validationSchema,
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,
    enableReinitialize: true,
    onSubmit: async values => {
      await mutateAsync(values.report);
    },
  });

  const userRole = rolesManipulator.current_user_role;
  const isUserAdmin = userRole === Role.COMPLIANCE_ADMIN;

  const handleOnSuccess = useCallback(
    async (data: CreateReportResponse) => {
      const { report } = data;

      // if (!templateId) {
      //   onClose();
      //   return;
      // }

      switch (report.status) {
        case ReportStatus.Completed:
          if (report.url) {
            programmaticallyDownloading(report.url, report.name);
          }
          onClose();
          break;

        case ReportStatus.Generating:
          setCreatedReportMeta({
            id: report.id,
            name: report.name,
            type: report.type,
            format: report.format,
          });
          break;

        default:
          onClose();
          break;
      }
    },
    [onClose],
  );

  const { mutateAsync, isLoading: isCreationLoading } = useCreateReportMutation({
    setErrors: formik.setErrors,
    onSuccess: handleOnSuccess,
  });

  const { data: template, isFetching: isTemplateFetching } = useGetTemplateQuery(
    templateId || 1,
    !templateId,
  );

  const pollEnabled = !!createdReportMeta; //&& !!templateId;
  usePollReportUntilReady(
    {
      id: createdReportMeta?.id,
      filter_by_name: createdReportMeta?.name,
      filter_by_format: createdReportMeta?.format,
      filter_by_type: createdReportMeta?.type,
      page: 1,
      page_size: 20,
    },
    {
      enabled: pollEnabled,
      onCompleted: report => {
        if (report.url) {
          openInNewTab(report.url);
          // programmaticallyDownloading(report.url, report.name);
        }
        setCreatedReportMeta(null);
        onClose();
      },
      onFailed: () => {
        setCreatedReportMeta(null);
        onClose();
      },
    },
  );

  useEffect(() => {
    if (template) {
      formik.setValues({
        registers: mapNameIdToSelectOption(template?.registers),
        users: (template?.assignees || []).map(({ id, first_name, last_name }) => ({
          label: `${first_name} ${last_name}`,
          value: id,
        })),
        forms: mapNameIdToSelectOption(template?.forms),
        departments: mapNameIdToSelectOption(template?.departments),
        incidents: mapNameIdToSelectOption(template?.incident_types),
        client: template.report_template?.client?.name
          ? {
              value: template.report_template?.client?.id,
              label: template.report_template?.client?.name,
            }
          : undefined,
        report: {
          ...omit(template.report_template, 'id'),
          date_range: defaultDates,
          client_id: template.report_template?.client?.id || clientId,
        },
      });
    }
  }, [template, clientId]);

  const handleChangeStartDate: CalendarInputProps['onChange'] = useCallback(
    (date: dayjs.Dayjs) => {
      const prevDate = formik.values.report.date_range[1] || null;
      if (!date) {
        formik.setFieldValue('report.date_range', [null, prevDate]);
        formik.setFieldError('report.date_range', '');
        return;
      }

      formik.setFieldValue('report.date_range', [date.format('YYYY-MM-DD'), prevDate]);
      formik.setFieldError('report.date_range', '');
    },
    [formik.values.report.date_range],
  );

  const handleChangeEndDate: CalendarInputProps['onChange'] = useCallback(
    (date: dayjs.Dayjs) => {
      const prevDate = formik.values.report.date_range[0] || null;
      if (!date) {
        formik.setFieldValue('report.date_range', [prevDate, null]);
        formik.setFieldError('report.date_range', '');
        return;
      }

      formik.setFieldValue('report.date_range', [prevDate, date.format('YYYY-MM-DD')]);
      formik.setFieldError('report.date_range', '');
    },
    [formik.values.report.date_range],
  );

  const handleChangeType = useCallback(
    (option: ReportType | null) => {
      formik.setFieldValue('report.type', option);
      formik.setFieldError('report.type', '');

      if (option !== ReportType.IncidentReport) {
        formik.setFieldValue('report.incident_type_ids', []);
      }
    },
    [formik],
  );

  const handleChangeRegister = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('registers', newValue);
      formik.setFieldValue(
        'report.register_id',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeAssignee = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('users', newValue);
      formik.setFieldValue(
        'report.assignee_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeForm = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('forms', newValue);
      formik.setFieldValue(
        'report.form_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeFormat = useCallback(
    (format: ReportFormat) => {
      formik.setFieldValue('report.format', format);
    },
    [formik],
  );

  const handleChangeDepartment = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('departments', newValue);
      formik.setFieldValue(
        'report.department_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeIncidentType = useCallback(
    (newValue: SelectPaginateOption<number>[]) => {
      formik.setFieldValue('incidents', newValue);
      formik.setFieldValue(
        'report.incident_type_ids',
        newValue.map(({ value }) => value),
      );
    },
    [formik],
  );

  const handleChangeClient = useCallback(
    (newValue: SelectPaginateOption<number> | null) => {
      formik.setFieldValue('client', newValue);
      formik.setFieldValue('report.client_id', newValue?.value || undefined);
    },
    [formik],
  );

  const handleSelectLastQuarter = useCallback(() => {
    const start = dayjs().subtract(3, 'months').startOf('quarter');
    const end = dayjs().subtract(3, 'months').endOf('quarter');

    formik.setFieldValue('report.date_range', [
      start.format('YYYY-MM-DD'),
      end.format('YYYY-MM-DD'),
    ]);
  }, [formik]);

  const handleSelectCurrentQuarter = useCallback(() => {
    const start = dayjs().startOf('quarter');
    const end = dayjs();

    formik.setFieldValue('report.date_range', [
      start.format('YYYY-MM-DD'),
      end.format('YYYY-MM-DD'),
    ]);
  }, [formik]);

  const handleSelectLastFinancialYear = useCallback(() => {
    const now = dayjs();
    const isFirstPartOfFinancialYear = dayjs().isBefore(new Date(2026, 7, 1));

    const startYear = isFirstPartOfFinancialYear ? now.year() - 2 : now.year() - 1;

    const start = dayjs(new Date(startYear, 6, 1));
    const end = dayjs(new Date(startYear + 1, 5, 30)).endOf('day');

    formik.setFieldValue('report.date_range', [
      start.format('YYYY-MM-DD'),
      end.format('YYYY-MM-DD'),
    ]);
  }, [formik]);

  const isStatusSelectVisible = useMemo(
    () =>
      formik.values.report.type === ReportType.TaskActivityReport ||
      formik.values.report.type === ReportType.TaskResultsReport,
    [formik.values.report.type],
  );

  useEffect(() => {
    if (!isStatusSelectVisible) {
      formik.setFieldValue('report.status', undefined);
    }
  }, [isStatusSelectVisible]);

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="Generate new report">
      <FormikProvider value={formik}>
        <form className="w-full" onSubmit={e => e.preventDefault()}>
          <div className="flex flex-col gap-8 select-none">
            <TextInputField
              name="report.name"
              label="REPORT NAME"
              placeholder="Enter report name..."
              disabled={isTemplateFetching}
            />

            <div className="relative w-full flex flex-col">
              <label className="text-xs text-[#475569] mb-2 leading-[18px] flex justify-between">
                <span className="text-gray-900 text-[10px] leading-[18px] font-meduim opacity-64 uppercase mb-2">
                  Date range
                </span>
                <div className="inline-flex items-center gap-4 text-[10px]">
                  <div
                    className="text-blue-700 hover:underline cursor-pointer"
                    onClick={handleSelectLastQuarter}
                  >
                    LAST QUARTER
                  </div>
                  <div className="h-[18px] border border-r" />
                  <div
                    className="text-blue-700 hover:underline cursor-pointer"
                    onClick={handleSelectCurrentQuarter}
                  >
                    CURRENT QUARTER
                  </div>
                  <div className="h-[18px] border border-r" />
                  <div
                    className="text-blue-700 hover:underline cursor-pointer"
                    onClick={handleSelectLastFinancialYear}
                  >
                    LAST FINANCIAL YEAR
                  </div>
                </div>
              </label>

              <div className="w-full flex flex-wrap sm:flex-nowrap items-start gap-6">
                <CalendarInput
                  allowClear={false}
                  name="StartDate"
                  disabled={isTemplateFetching}
                  onChange={handleChangeStartDate}
                  error={formik.errors?.report?.date_range?.[0]}
                  value={
                    formik.values?.report?.date_range?.[0]
                      ? dayjs(formik.values?.report?.date_range?.[0])
                      : null
                  }
                />

                <CalendarInput
                  allowClear={false}
                  name="EndDate"
                  disabled={isTemplateFetching}
                  onChange={handleChangeEndDate}
                  error={formik.errors?.report?.date_range?.[1]}
                  value={
                    formik.values?.report?.date_range?.[1]
                      ? dayjs(formik.values?.report?.date_range?.[1])
                      : null
                  }
                />
              </div>
            </div>
            <ReportTypeSelect
              hideBatch
              disabled={isTemplateFetching}
              value={formik.values.report.type}
              onChange={handleChangeType}
            />
            {isStatusSelectVisible && (
              <SelectButtonsNew
                label="STATUS (optional)"
                value={formik.values.report.status}
                options={taskResultOptions}
                onChange={status =>
                  formik.setFieldValue(
                    'report.status',
                    formik.values.report.status === status ? undefined : status,
                  )
                }
              />
            )}
            <RegisterSelectPaginate
              isMulti={true}
              disabled={isTemplateFetching}
              label="Label (optional)"
              onChange={handleChangeRegister}
              value={formik.values.registers}
            />
            <UsersSelectPaginated
              isMulti={true}
              disabled={isTemplateFetching}
              onChange={handleChangeAssignee}
              label="User (optional)"
              value={formik.values.users}
            />
            {isUserAdmin && (
              <FormSelectPaginate
                disabled={isTemplateFetching}
                isMulti={true}
                formFieldTitle="form_ids"
                label="Form (optional)"
                onChange={handleChangeForm}
                value={formik.values.forms}
              />
            )}

            <SelectButtonsNew
              disabled={isTemplateFetching}
              label="FORMAT"
              value={formik.values.report.format}
              options={reportsFormats}
              onChange={handleChangeFormat}
            />

            <DepartmentsSelectPaginated
              disabled={isTemplateFetching}
              isMulti={true}
              handleSelectDepartments={handleChangeDepartment}
              label="Department (optional)"
              formFieldTitle="department_ids"
              value={formik.values.departments}
            />

            {!clientId && (
              <ClientsSelectPaginated
                disabled={isTemplateFetching}
                isMulti={false}
                onChange={handleChangeClient}
                value={formik.values.client}
                isClearable={true}
              />
            )}

            {formik.values.report.type === ReportType.IncidentReport && (
              <IncidentTypeSelectPaginated
                disabled={isTemplateFetching}
                isMulti={true}
                onChange={handleChangeIncidentType}
                label="Event Type (optional)"
                formFieldTitle="incident_type_ids"
                value={formik.values.incidents}
              />
            )}

            <Button
              variant="primary"
              type="submit"
              onClick={formik.submitForm}
              loading={isCreationLoading || !!createdReportMeta}
              disabled={isCreationLoading || !!createdReportMeta || isTemplateFetching}
            >
              Generate
            </Button>
          </div>
        </form>
      </FormikProvider>
    </ModalContentWrapper>
  );
};

export default CreateReportModal;
